/**
 * Aggregate Accounts — Lab `#!/aggregate/accounts` in Strix People rail.
 * Root memberList only (old beta AggregatorAccountsCtrl — no username drill).
 */

import { useEffect, useMemo, useState } from 'react'
import {
  Banner,
  Card,
  Chip,
  DataTable,
  DEFAULT_PAGE_SIZE,
  PageHead,
  Pager,
  SearchInput,
  StateBlock,
  useClientPagination,
  type Column,
  type Tone,
} from '@w7/ui'
import { Amount, Money } from '@/app/bits'
import { useAggregateAccounts } from '@/api/hooks/aggregate/useAggregateAccounts'
import {
  labAggregateAccountsColumns,
  takeGiveFromBalanceUp,
  type AggregateAccount,
} from '@/api/map/aggregateAccounts'
import { isDemoSession } from '@/auth/oidc'
import type { AccountStatus } from '@/data/types'
import './aggregate-accounts.css'

const SEARCH_DEBOUNCE_MS = 200

/** Aggregate console viewer — Lab profile.accessLevel === 'Aggregate'. */
const VIEWER_ACCESS_LEVEL = 'Aggregate'

const STATUS: Record<AccountStatus, { label: string; tone: Tone }> = {
  active: { label: 'Active', tone: 'info' },
  inactive: { label: 'Inactive', tone: 'muted' },
  suspended: { label: 'Suspended', tone: 'warn' },
  closed: { label: 'Closed', tone: 'muted' },
}

function statusMeta(status: AccountStatus): { label: string; tone: Tone } {
  return STATUS[status] ?? { label: status, tone: 'muted' }
}

export function AggregateAccountsPage() {
  const [draft, setDraft] = useState('')
  const [username, setUsername] = useState('')
  const query = useAggregateAccounts(username)
  const demo = isDemoSession()
  const { showCurrency, showAvailableCredit } = labAggregateAccountsColumns(VIEWER_ACCESS_LEVEL)

  useEffect(() => {
    const t = window.setTimeout(() => setUsername(draft), SEARCH_DEBOUNCE_MS)
    return () => window.clearTimeout(t)
  }, [draft])

  const allRows = query.data?.rows ?? []
  const {
    page,
    pageCount,
    pageSize,
    total,
    rows,
    setPage,
  } = useClientPagination(allRows, {
    pageSize: DEFAULT_PAGE_SIZE,
    resetKey: username,
  })

  const columns = useMemo<Column<AggregateAccount>[]>(() => {
    const cols: Column<AggregateAccount>[] = [
      {
        id: 'index',
        header: '#',
        cell: (_row, index) => <span className="w7-agg-index">{index + 1}</span>,
      },
      {
        id: 'username',
        header: 'UserName',
        cell: (row) => (
          <div className="w7-agg-user">
            <span className="w7-agg-username">{row.username}</span>
          </div>
        ),
      },
    ]

    if (showCurrency) {
      cols.push({
        id: 'currency',
        header: 'Currency',
        cell: (row) => (
          <span className="w7-agg-currency">{row.currencyType ?? 'INR'}</span>
        ),
      })
    }

    cols.push(
      {
        id: 'creditLimit',
        header: 'Credit Limit',
        align: 'right',
        numeric: true,
        cell: (row) => (
          <Amount value={row.creditLimit} currency={row.currencyType} alreadyDisplay />
        ),
      },
      {
        id: 'take',
        header: 'Take',
        align: 'right',
        numeric: true,
        help: 'Lab balanceUp < 0 — what they owe you.',
        cell: (row) => {
          const { take } = takeGiveFromBalanceUp(row.balanceUp)
          return (
            <Money
              value={take}
              tone={take > 0 ? 'profit' : 'neutral'}
              currency={row.currencyType}
              alreadyDisplay
            />
          )
        },
      },
      {
        id: 'give',
        header: 'Give',
        align: 'right',
        numeric: true,
        help: 'Lab balanceUp > 0 — what you owe them.',
        cell: (row) => {
          const { give } = takeGiveFromBalanceUp(row.balanceUp)
          return (
            <Money
              value={give}
              tone={give > 0 ? 'loss' : 'neutral'}
              currency={row.currencyType}
              alreadyDisplay
            />
          )
        },
      },
    )

    if (showAvailableCredit) {
      cols.push({
        id: 'availableCredit',
        header: 'Available Credit',
        align: 'right',
        numeric: true,
        help: 'Lab Available Credit = creditLimit + balanceUp − exposure.',
        cell: (row) => (
          <Amount value={row.availableCredit} currency={row.currencyType} alreadyDisplay />
        ),
      })
    }

    cols.push({
      id: 'status',
      header: 'Status',
      cell: (row) => {
        const meta = statusMeta(row.status)
        return <Chip tone={meta.tone}>{meta.label}</Chip>
      },
    })

    return cols
  }, [showAvailableCredit, showCurrency])

  return (
    <div className="w7-agg-accounts-page">
      <PageHead
        title="Aggregate Accounts"
        subtitle="Members linked to this Aggregate group — credit, Take/Give, and status."
      />

      {demo ? (
        <Banner tone="info">
          Demo accounts — sign in against Aggregate-API for live memberList search.
        </Banner>
      ) : null}

      <Card className="w7-agg-accounts-card" flush>
        <div className="w7-agg-accounts-toolbar">
          <SearchInput
            value={draft}
            onChange={setDraft}
            placeholder="Search"
            label="Search Aggregate accounts by username"
            width={280}
          />
        </div>

        {query.isError ? (
          <StateBlock icon="warning" title="Could not load Aggregate Accounts" tone="error">
            {query.error instanceof Error ? query.error.message : 'Request failed'}
          </StateBlock>
        ) : null}

        {!query.isError && allRows.length === 0 && !query.isLoading ? (
          <StateBlock icon="users" title="No data found">
            {username.trim()
              ? `No accounts match “${username.trim()}”.`
              : 'This Aggregate group has no members in its list yet.'}
          </StateBlock>
        ) : null}

        {!query.isError && allRows.length > 0 ? (
          <>
            <DataTable
              label="Aggregate Accounts"
              columns={columns}
              rows={rows}
              rowKey={(row) => row.id}
              loading={query.isFetching}
            />
            <Pager
              page={page}
              pageCount={pageCount}
              onChange={setPage}
              total={total}
              pageSize={pageSize}
            />
          </>
        ) : null}

        {query.isLoading && allRows.length === 0 ? (
          <StateBlock icon="users" title="Loading…">
            Fetching Aggregate Accounts.
          </StateBlock>
        ) : null}
      </Card>
    </div>
  )
}
